<script setup>
import { ref, onMounted, computed, nextTick, h } from 'vue';
import { useRouter } from 'vue-router';
import dayjs from 'dayjs';
import { message } from 'ant-design-vue';
import {
    PlusOutlined,
    SearchOutlined
} from '@ant-design/icons-vue';
import formatDateTime from '@/utils/formatDateTime';
import { taipeiDay } from '@/utils/taipeiTime';

// 權限控制
import { usePermission } from '@/composables/usePermission';

// 權限檢查
const { canView, canCreate, canUpdate, canDelete, canCopy, canExport } =
    usePermission('FinanceVersionList');

// 檢查是否有任何操作權限（包含查看權限）
const hasAnyActionPermission = computed(() => {
    return (
        canView.value || canUpdate.value || canDelete.value || canExport.value
    );
});

// 路由器
const router = useRouter();

// API
import { getFinanceVersions } from '@/api/finance-version/versionList';
import { userList } from '@/api/admin/userList';
import { deleteVersion } from '@/api/finance-version/deleteVersion';
import { createVersion } from '@/api/finance-version/createVersion';
import { copyVersion } from '@/api/finance-version/copyVersion';
import { exportVersion } from '@/api/finance-version/exportVersion';
import { createUserDetailVersion } from '@/api/user-detail-version/createUserDetailVersion';
import { getConclusionByVersion } from '@/api/consultation-workspace/consultationWorkspaceApi';
import { buildConsultationAdjustment } from '@/utils/consultationAdjustment';

// 元件
import DataTable from '@/components/common/DataTable.vue';
import BaseDialogDelete from '@/components/base/BaseDialogDelete.vue';

// 數字格式化（列表展示）
const formatNumber = (n) => {
    const num = Number(n ?? 0);
    return Number.isFinite(num)
        ? num.toLocaleString('zh-TW', { maximumFractionDigits: 2 })
        : '-';
};

const cashFlowClass = (value) => {
    const num = Number(value ?? 0);
    if (!Number.isFinite(num) || num === 0) return 'finance-num--neutral';
    return num > 0 ? 'finance-num--positive' : 'finance-num--negative';
};

const consultationAdjustments = ref({});
const adjustmentFor = (record, key) => consultationAdjustments.value[record.id]?.[key];
const loadConsultationAdjustments = async (records) => {
    const completed = records.filter((record) => record.has_consultation);
    // 序列化／小批次，避免列表一載就打爆 throttle
    const next = { ...consultationAdjustments.value };
    const chunkSize = 3;
    for (let i = 0; i < completed.length; i += chunkSize) {
        const chunk = completed.slice(i, i + chunkSize);
        const results = await Promise.allSettled(
            chunk.map((record) => getConclusionByVersion(record.id))
        );
        results.forEach((result, index) => {
            if (result.status !== 'fulfilled' || result.value?.status !== 1) return;
            next[chunk[index].id] = buildConsultationAdjustment(
                result.value.data?.metrics
            );
        });
    }
    consultationAdjustments.value = next;
};

// 此列表固定學員來源（source = 0）

// 搜尋表單狀態
const searchForm = ref({
    // 日期區間（更新時間）
    updated_at_range: [], // [startDayjs, endDayjs]
    start_updated_at: '',
    end_updated_at: '',

    // 其他條件
    status: '', // 0/1，預設全部
    requires_booking: '', // 0/1，預設全部
    user_id: '',
    user_id_display: '',
    internal_name: '',
    is_visible_user: '', // 0/1，預設全部
    source: '', // 0/1/2，預設全部

    // 數值區間
    net_asset_value_min: '',
    net_asset_value_max: '',
    monthly_cash_flow_min: '',
    monthly_cash_flow_max: ''
});

// 新增彈窗狀態
const addOpen = ref(false);
const addTab = ref('new'); // 'new' | 'copy'
const addLoading = ref(false);

// 新增：學員搜尋
const addUserId = ref('');
const addUserOptions = ref([]);
const addUserLoading = ref(false);

// 防抖處理函數
let searchDebounceTimer = null;
const DEBOUNCE_DELAY = 300; // 300ms 防抖延遲

const searchUsers = async (keyword) => {
    // 清除之前的定時器
    if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
    }

    // 如果關鍵字為空，立即執行（不需要防抖）
    if (!keyword || keyword.trim() === '') {
        addUserLoading.value = true;
        try {
            const res = await userList(20, 1, {});
            const list = Array.isArray(res?.data) ? res.data : [];
            // 將 id 存到 value，名稱存到 label
            addUserOptions.value = list.map((u) => ({
                value: String(u.id), // value 存 id
                label: u.name || `學員${u.id}` // label 存名稱
            }));
        } catch (e) {
            console.error('searchUsers error', e);
            message.error('搜尋學員失敗，請重試');
        } finally {
            addUserLoading.value = false;
        }
        return;
    }

    // 有關鍵字時使用防抖
    addUserLoading.value = true;
    searchDebounceTimer = setTimeout(async () => {
        try {
            const res = await userList(20, 1, { name: keyword.trim() });
            const list = Array.isArray(res?.data) ? res.data : [];
            // 將 id 存到 value，名稱存到 label
            addUserOptions.value = list.map((u) => ({
                value: String(u.id), // value 存 id
                label: u.name || `學員${u.id}` // label 存名稱
            }));
        } catch (e) {
            console.error('searchUsers error', e);
            message.error('搜尋學員失敗，請重試');
        } finally {
            addUserLoading.value = false;
            searchDebounceTimer = null;
        }
    }, DEBOUNCE_DELAY);
};

// 複製：版本搜尋（依學員載入）
const addCopyVersionId = ref('');
const addCopyVersionOptions = ref([]);
const addCopyLoading = ref(false);
const addCopyVersionDisplay = ref(''); // 新增顯示用的版本名稱
// 版本快取
const versionCache = ref({}); // { [user_id]: { data: [], fetchedAt: timestamp } }
const VERSION_CACHE_TTL = 3 * 60 * 1000; // 3 分鐘

// 防抖處理
let searchVersionDebounceTimer = null;
const VERSION_DEBOUNCE_DELAY = 300;

// 複製按鈕處理
const handleCopy = async (record) => {
    addOpen.value = true;
    addTab.value = 'copy';

    // 使用 nextTick 確保 DOM 更新後再載入選項
    await nextTick();

    // 先載入學員選項
    await searchUsers('');

    // 從 user_name 反查 user_id
    const userName = record.user_name || '';

    // 從選項中找到對應的 user_id
    const matchedUser = addUserOptions.value.find(
        (opt) => opt.label === userName
    );

    if (matchedUser) {
        addUserId.value = matchedUser.value;
    } else {
        // 如果找不到，嘗試用 user_name 搜尋
        await searchUsers(userName);
        const searchedUser = addUserOptions.value.find(
            (opt) => opt.label === userName
        );

        if (searchedUser) {
            addUserId.value = searchedUser.value;
        } else {
            message.warning('無法找到對應的學員');
            return;
        }
    }

    // 再次使用 nextTick 確保學員選項載入完成
    await nextTick();

    // 載入該學員的版本選項
    await loadCopyOptions();

    // 設定版本選項和顯示
    addCopyVersionId.value = String(record.id);
    const versionName =
        (record.internal_name || '-') +
        (record.external_name ? ` 【${record.external_name}】` : '');
    addCopyVersionDisplay.value = versionName;

    // 設定表單預設值
    addForm.value = {
        external_name: '',
        internal_name: '',
        source: 0,
        is_visible_user: true
    };
    addPersonal.value = { age: '', job: '', marital_status: 0, children: 0 };
};

// 搜尋版本（動態載入）
const searchVersions = async (keyword) => {
    if (!addUserId.value) {
        addCopyVersionOptions.value = [];
        return;
    }

    // 清除之前的定時器
    if (searchVersionDebounceTimer) {
        clearTimeout(searchVersionDebounceTimer);
    }

    const userId = Number(addUserId.value);
    const now = Date.now();
    
    // 檢查快取
    const cached = versionCache.value[userId];
    const freshEnough = cached && cached.fetchedAt && (now - cached.fetchedAt < VERSION_CACHE_TTL);
    
    // 如果關鍵字為空且有快取，使用快取
    if ((!keyword || keyword.trim() === '') && freshEnough) {
        addCopyVersionOptions.value = cached.data;
        return;
    }

    // 有關鍵字時使用防抖
    addCopyLoading.value = true;
    searchVersionDebounceTimer = setTimeout(async () => {
        try {
            const params = {
                user_id: userId,
                page: 1,
                page_size: 100, // 載入更多以支援搜尋
                source: 0
            };
            
            const res = await getFinanceVersions(params);
            const allData = (Array.isArray(res?.data) ? res.data : []).filter(
                (version) => Number(version.source) === 0
            );
            
            // 過濾符合關鍵字的版本
            let filteredData = allData;
            if (keyword && keyword.trim()) {
                const keywordLower = keyword.trim().toLowerCase();
                filteredData = allData.filter((v) => {
                    const internalName = (v.internal_name || '').toLowerCase();
                    const externalName = (v.external_name || '').toLowerCase();
                    const id = String(v.id);
                    return (
                        internalName.includes(keywordLower) ||
                        externalName.includes(keywordLower) ||
                        id.includes(keywordLower)
                    );
                });
            }
            
            const mapped = filteredData.map((v) => ({
                value: String(v.id),
                label:
                    (v.internal_name || '-') +
                    (v.external_name ? ` 【${v.external_name}】` : '')
            }));
            
            addCopyVersionOptions.value = mapped;
            
            // 更新快取（僅在無關鍵字時）
            if (!keyword || !keyword.trim()) {
                versionCache.value[userId] = {
                    data: mapped,
                    fetchedAt: now
                };
            }
        } catch (e) {
            console.error('搜尋版本失敗:', e);
            message.error('載入版本列表失敗，請重試');
            addCopyVersionOptions.value = [];
        } finally {
            addCopyLoading.value = false;
            searchVersionDebounceTimer = null;
        }
    }, VERSION_DEBOUNCE_DELAY);
};

// 載入版本選項（保持向後相容）
const loadCopyOptions = async () => {
    await searchVersions('');
};

// 新增/複製 共同欄位
const addForm = ref({
    external_name: '',
    internal_name: '',
    source: 0,
    is_visible_user: true
});

// 全新建立：個人訊息欄位
const addPersonal = ref({ age: '', job: '', marital_status: 0, children: 0 });

// 表單引用
const addFormRef = ref();

// 重置新增表單
const resetAddForm = () => {
    // 清除防抖定時器
    if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
        searchDebounceTimer = null;
    }
    
    addTab.value = 'new';
    addUserId.value = '';
    addCopyVersionId.value = '';
    addCopyVersionDisplay.value = '';
    addUserOptions.value = [];
    addCopyVersionOptions.value = [];
    addForm.value = {
        external_name: '',
        internal_name: '',
        source: 0,
        is_visible_user: true
    };
    addPersonal.value = { age: '', job: '', marital_status: 0, children: 0 };
    // 重置表單驗證狀態
    addFormRef.value?.resetFields?.();
};

// 打開新增彈窗
const handleAdd = async () => {
    // 先清空所有資料
    resetAddForm();

    // 使用 nextTick 確保 DOM 更新後再打開視窗
    await nextTick();

    addOpen.value = true;

    // 再載入學員選項
    await nextTick();
    await searchUsers('');
};

// 統一錯誤處理函數
const handleApiError = (error, defaultMessage = '操作失敗') => {
    // 處理驗證錯誤
    if (
        error?.response?.data?.errors &&
        typeof error.response.data.errors === 'object'
    ) {
        const errorMessages = Object.values(error.response.data.errors)
            .flat()
            .join('；');
        return message.error(
            errorMessages || error?.response?.data?.message || defaultMessage
        );
    }
    // 處理一般錯誤
    if (error?.errors && typeof error.errors === 'object') {
        const errorMessages = Object.values(error.errors)
            .flat()
            .join('；');
        return message.error(
            errorMessages || error?.message || defaultMessage
        );
    }
    // 回傳錯誤訊息
    return message.error(
        error?.response?.data?.message ||
            error?.message ||
            error?.data?.message ||
            defaultMessage
    );
};

// 提取版本 ID 的統一函數（兼容多層包裹格式）
const extractVersionId = (res) => {
    return (
        res?.data?.id ??
        res?.id ??
        res?.data?.data?.id ??
        res?.original?.data?.id ??
        res?.data?.original?.data?.id ??
        res?.data?.data?.original?.data?.id ??
        null
    );
};

// 提取狀態的統一函數
const extractStatus = (res) => {
    return (
        res?.status ??
        res?.data?.status ??
        res?.original?.status ??
        res?.data?.original?.status ??
        null
    );
};

const submitAdd = async () => {
    try {
        // 表單驗證
        await addFormRef.value?.validate?.();

        // 檢查學員 ID
        if (!addUserId.value || addUserId.value === '') {
            return message.warning('請先選擇學員');
        }

        // 檢查必填欄位（額外驗證，確保至少填寫一項）
        if (
            !addForm.value.external_name?.trim() &&
            !addForm.value.internal_name?.trim()
        ) {
            return message.warning('對外名稱或對內名稱至少需填寫一項');
        }

        if (addTab.value === 'copy' && !addCopyVersionId.value) {
            return message.warning('請選擇要複製的版本');
        }

        addLoading.value = true;
        if (addTab.value === 'new') {
            // 先建立個人訊息，取得 user_detail_id
            const udvPayload = {
                user_id: Number(addUserId.value),
                age: Number(addPersonal.value.age || 0),
                job: (addPersonal.value.job || '').trim(),
                marital_status: Number(addPersonal.value.marital_status || 0),
                children: Number(addPersonal.value.children || 0)
            };
            const udvRes = await createUserDetailVersion(udvPayload);
            const userDetailId =
                udvRes?.data?.user_detail_id ||
                udvRes?.data?.id ||
                udvRes?.user_detail_id;
            if (!(udvRes?.status === 1 && userDetailId)) {
                handleApiError(udvRes, '建立個人訊息失敗');
                addLoading.value = false; // 確保重置 loading 狀態
                return;
            }
            // 取得 user_detail_id 後建立版本
            const res = await createVersion({
                user_id: Number(addUserId.value),
                user_detail_id: Number(userDetailId),
                external_name: addForm.value.external_name?.trim(),
                internal_name: addForm.value.internal_name?.trim(),
                source: 0,
                is_visible_user: Number(Boolean(addForm.value.is_visible_user))
            });
            const status = extractStatus(res);
            const newId = extractVersionId(res);
            if (status === 1 && newId) {
                message.success('建立成功');
                addOpen.value = false;
                resetAddForm();
                // 使用 router.push 而不是 window.location.href，避免整頁重新載入
                await router.push({
                    name: 'FinanceVersionEdit',
                    params: { id: newId },
                    query: { fresh: '1' }
                });
                return;
            }
            handleApiError(res, '建立版本失敗');
        } else {
            // 複製模式
            if (!addCopyVersionId.value) {
                return message.warning('請選擇要複製的版本');
            }
            const res = await copyVersion(Number(addCopyVersionId.value), {
                external_name: addForm.value.external_name?.trim(),
                internal_name: addForm.value.internal_name?.trim(),
                source: 0,
                is_visible_user: 1
            });
            const status = extractStatus(res);
            const newId = extractVersionId(res);
            if (status === 1 && newId) {
                message.success('複製成功');
                addOpen.value = false;
                resetAddForm();
                // 使用 router.push 而不是 window.location.href
                await router.push({
                    name: 'FinanceVersionEdit',
                    params: { id: newId }
                });
                return;
            }
            handleApiError(res, '複製失敗');
        }
    } catch (e) {
        console.error('submitAdd error', e);
        // 如果是表單驗證錯誤，不顯示 API 錯誤訊息
        if (e?.errorFields) {
            // 表單驗證錯誤已在表單中顯示，這裡不需要額外處理
            return;
        }
        handleApiError(e, '操作失敗');
    } finally {
        addLoading.value = false;
    }
};

// 選項資料
const userIdOptions = ref([]);

const usersFromListResponse = (res) =>
    Array.isArray(res?.data) ? res.data : [];

const toUserOptions = (list) =>
    list.map((u) => ({
        value: u.name || `學員${u.id}`,
        userId: String(u.id),
        label: u.name || `學員${u.id}`
    }));

// 取得會員選項（列表搜尋用）
const getUserOptions = async () => {
    try {
        const res = await userList(20, 1, {});
        userIdOptions.value = toUserOptions(usersFromListResponse(res));
    } catch (e) {
        console.error('獲取會員列表失敗', e);
    }
};

// 搜尋會員（列表搜尋用，支援動態搜尋）
let searchUserDebounceTimer = null;
const searchUserForList = async (keyword) => {
    // 清除之前的定時器
    if (searchUserDebounceTimer) {
        clearTimeout(searchUserDebounceTimer);
    }

    // 如果關鍵字為空，載入前 20 筆
    if (!keyword || keyword.trim() === '') {
        await getUserOptions();
        return;
    }

    // 有關鍵字時使用防抖
    searchUserDebounceTimer = setTimeout(async () => {
        try {
            const res = await userList(20, 1, { name: keyword.trim() });
            userIdOptions.value = toUserOptions(usersFromListResponse(res));
        } catch (e) {
            console.error('搜尋會員失敗', e);
        } finally {
            searchUserDebounceTimer = null;
        }
    }, DEBOUNCE_DELAY);
};

// 表格資料
const tableData = ref([]);
const loading = ref(false);

// 分頁
const pagination = ref({
    current: 1,
    pageSize: 10,
    total: 0
});

// 欄位
const columns = computed(() => {
    const baseColumns = [
        {
            title: '版本名稱',
            dataIndex: 'names',
            key: 'names',
            width: 280,
            fixed: 'left',
            align: 'left'
        },
        {
            title: '會員',
            dataIndex: 'user_name',
            key: 'user_name',
            width: 120,
            align: 'left'
        },
        {
            title: '總資產',
            dataIndex: 'total_assets',
            key: 'total_assets',
            width: 120,
            align: 'right'
        },
        {
            title: '總負債',
            dataIndex: 'total_liabilities',
            key: 'total_liabilities',
            width: 120,
            align: 'right'
        },
        {
            title: '淨資產 前→後',
            dataIndex: 'net_asset_value',
            key: 'net_asset_value',
            width: 200,
            align: 'right'
        },
        {
            title: '現金流 前→後',
            key: 'monthly_cash_flow',
            width: 200,
            align: 'right'
        },
        {
            title: '月收入',
            dataIndex: 'total_monthly_income',
            key: 'total_monthly_income',
            width: 110,
            align: 'right'
        },
        {
            title: '月支出',
            dataIndex: 'total_monthly_expenses',
            key: 'total_monthly_expenses',
            width: 110,
            align: 'right'
        },
        {
            title: '建立時間',
            dataIndex: 'created_at',
            key: 'created_at',
            width: '160px',
            align: 'center'
        },
        {
            title: '更新時間',
            dataIndex: 'updated_at',
            key: 'updated_at',
            width: '160px',
            align: 'center'
        }
    ];

    // 如果有任何操作權限，才加入操作欄位
    if (hasAnyActionPermission.value) {
        baseColumns.push({
            title: '操作',
            key: 'actions',
            width: 160,
            fixed: 'right',
            align: 'center'
        });
    }

    return baseColumns;
});

/** 對外名稱與對內相同時不重複顯示，避免名稱欄多一行 */
const distinctExternalName = (record) => {
    const internal = String(record?.internal_name || '').trim();
    const external = String(record?.external_name || '').trim();
    if (!external) return '';
    if (external === internal) return '';
    return external;
};

/** 列表預約摘要：兩行（諮詢師 / 短時段），狀態已有 tag 不再重複 */
const bookingMeta = (record) => {
    const b = record?.consultation_booking;
    if (!b?.starts_at) return null;
    const consultant = String(
        b.consultant_name || b.consultant_core_member_id || ''
    ).trim();
    const d = taipeiDay(b.starts_at);
    const when = d.isValid() ? d.format('M/D HH:mm') : formatDateTime(b.starts_at);
    const mode = b.mode === 'offline' ? '實體' : b.mode === 'online' ? '線上' : '';
    const title = [consultant, formatDateTime(b.starts_at), mode]
        .filter(Boolean)
        .join(' · ');
    return {
        consultant,
        when,
        mode,
        title,
        consultationId: b.id || null
    };
};

/** 從版本列進預約紀錄／編輯細節 */
const goConsultationFromVersion = (record, event) => {
    event?.stopPropagation?.();
    const meta = bookingMeta(record);
    if (!meta?.consultationId) return;
    router.push({
        name: 'Consultations',
        query: {
            version_id: String(record.id),
            consultation_id: String(meta.consultationId)
        }
    });
};

// 取得列表
const getVersionList = async (page = 1, pageSize = 10) => {
    loading.value = true;
    try {
        const params = buildQueryParams({ page, page_size: pageSize });
        const res = await getFinanceVersions(params);

        if (res?.status === 1 && res?.data) {
            // API 已統一為格式 A：data 直接是陣列
            tableData.value = Array.isArray(res.data) ? res.data : [];
            await loadConsultationAdjustments(tableData.value);
            pagination.value.total = res.total || 0;
            pagination.value.current = res.page || page;
            pagination.value.pageSize = res.page_size || pageSize;
        } else {
            tableData.value = [];
            pagination.value.total = 0;
            pagination.value.current = 1;
            message.error(res?.message || '獲取資料失敗');
        }
    } catch (error) {
        console.error('Get Finance Versions Error:', error);
        message.error('獲取資料失敗，請重試');
    } finally {
        loading.value = false;
    }
};

// 建立查詢參數
const buildQueryParams = (base = {}) => {
    const params = { ...base };

    // 日期區間
    if (searchForm.value.updated_at_range?.length === 2) {
        const [start, end] = searchForm.value.updated_at_range;
        params.start_updated_at = dayjs(start).format('YYYY-MM-DD');
        params.end_updated_at = dayjs(end).format('YYYY-MM-DD');
    }

    // 其他條件（僅在有值時帶上）
    if (searchForm.value.status !== '')
        params.status = Number(searchForm.value.status);
    if (searchForm.value.requires_booking !== '')
        params.requires_booking = Number(searchForm.value.requires_booking);
    if (searchForm.value.user_id)
        params.user_id = Number(searchForm.value.user_id);
    if (searchForm.value.internal_name)
        params.internal_name = searchForm.value.internal_name.trim();
    // 此列表固定只顯示學員來源，不開放切換
    params.source = 0;

    // 數值區間
    if (searchForm.value.net_asset_value_min !== '')
        params.net_asset_value_min = Number(
            searchForm.value.net_asset_value_min
        );
    if (searchForm.value.net_asset_value_max !== '')
        params.net_asset_value_max = Number(
            searchForm.value.net_asset_value_max
        );
    if (searchForm.value.monthly_cash_flow_min !== '')
        params.monthly_cash_flow_min = Number(
            searchForm.value.monthly_cash_flow_min
        );
    if (searchForm.value.monthly_cash_flow_max !== '')
        params.monthly_cash_flow_max = Number(
            searchForm.value.monthly_cash_flow_max
        );

    return params;
};

const resolveMemberFilter = async () => {
    const name = String(searchForm.value.user_id_display || '').trim();
    if (!name) {
        searchForm.value.user_id = '';
        return true;
    }
    const selected = userIdOptions.value.find(
        (option) =>
            String(option.userId) === String(searchForm.value.user_id) &&
            option.label === name
    );
    if (selected) return true;

    const res = await userList(20, 1, { name });
    const list = usersFromListResponse(res);
    const exact = list.filter((user) => String(user.name || '').trim() === name);
    const pick = exact.length === 1 ? exact[0] : list.length === 1 ? list[0] : null;
    if (!pick) {
        message.warning(
            list.length > 1 ? '有多位同名會員，請從清單選一位' : '找不到這位會員'
        );
        return false;
    }
    searchForm.value.user_id = String(pick.id);
    searchForm.value.user_id_display = pick.name || name;
    return true;
};

// 搜尋
const handleSearch = async () => {
    const ready = await resolveMemberFilter();
    if (!ready) return;
    await getVersionList(1, pagination.value.pageSize);
};

// 重置
const searchFormKey = ref(0);
const handleReset = () => {
    searchForm.value = {
        updated_at_range: [],
        start_updated_at: '',
        end_updated_at: '',
        status: '',
        requires_booking: '',
        user_id: '',
        user_id_display: '',
        internal_name: '',
        is_visible_user: '',
        source: '',
        net_asset_value_min: '',
        net_asset_value_max: '',
        monthly_cash_flow_min: '',
        monthly_cash_flow_max: ''
    };
    searchFormKey.value++;
    getVersionList(1, pagination.value.pageSize);
};

// 分頁切換
const handlePaginationChange = async (page, pageSize) => {
    await getVersionList(page, pageSize);
};

// 右上角新增 & 行內操作
const handleView = (record) => {
    router.push({
        name: 'FinanceVersionView',
        params: { id: record.id },
        query: { readonly: '1' }
    });
};
const handleEdit = (record) => {
    router.push({
        name: 'FinanceVersionEdit',
        params: { id: record.id }
    });
};
const handleOpen = (record) => handleView(record);

// 匯出處理
const handleExport = async (record) => {
    try {
        const blob = await exportVersion(record.id);
        if (blob?.type && String(blob.type).includes('application/json')) {
            const text = await blob.text();
            const parsed = JSON.parse(text || '{}');
            throw new Error(parsed.message || '匯出失敗');
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `資產負債表_${record.user_name}_${
            record.internal_name || record.external_name || record.id
        }.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        message.success('匯出成功（僅含已完成的原始／盤點／諮詢版）');
    } catch (e) {
        console.error('Export Version Error:', e);
        message.error(e?.message || '匯出失敗，請重試');
    }
};

// 刪除彈窗狀態與處理（與管理者列表一致樣式）
const deleteOpen = ref(false);
const deleteRecord = ref(null);
const handleDelAction = (record) => {
    deleteRecord.value = record;
    deleteOpen.value = true;
};
const handleDeleteConfirm = async (isConfirm) => {
    if (!isConfirm) {
        deleteOpen.value = false;
        return;
    }
    try {
        const res = await deleteVersion(deleteRecord.value?.id);
        if (res && res.status === 1) {
            message.success('刪除成功');
            await getVersionList(
                pagination.value.current,
                pagination.value.pageSize
            );
        } else {
            message.error(res?.message || '刪除失敗');
        }
    } catch (e) {
        console.error('Delete Version Error:', e);
        message.error('刪除失敗，請重試');
    } finally {
        deleteOpen.value = false;
        deleteRecord.value = null;
    }
};

onMounted(() => {
    getUserOptions();
    getVersionList(1, 10);
});
</script>

<template>
    <div class="finance-version-list space-y-5">
        <!-- 刪除確認彈窗（樣式與管理者列表一致） -->
        <BaseDialogDelete
            v-model:open="deleteOpen"
            title="確認刪除版本"
            :content="`姓名：<span class='text-red-500 font-bold'>${
                deleteRecord?.user_name || '-'
            }</span><br/>版本名稱：<span class='text-red-500 font-bold'>${
                deleteRecord?.internal_name ||
                deleteRecord?.external_name ||
                `#${deleteRecord?.id || ''}`
            }</span><br/>此操作無法復原。`"
            ok-text="確認刪除"
            cancel-text="取消"
            :width="520"
            bodyHeight="auto"
            @confirm="handleDeleteConfirm"
        />

        <!-- 搜尋區塊 -->
        <a-card
            :bordered="false"
            class="admin-panel admin-filter-panel admin-filter-panel--compact"
        >
            <a-form layout="vertical" class="admin-filter-form">
                <div class="admin-filter-grid version-filter-grid">
                    <a-form-item label="更新日期" class="!mb-0">
                        <a-range-picker
                            v-model:value="searchForm.updated_at_range"
                            value-format="YYYY-MM-DD"
                            :placeholder="['開始', '結束']"
                            :allowClear="true"
                            class="w-full min-w-0"
                        />
                    </a-form-item>

                    <a-form-item label="會員" class="!mb-0">
                        <a-auto-complete
                            v-model:value="searchForm.user_id_display"
                            placeholder="姓名"
                            :options="userIdOptions"
                            allowClear
                            class="w-full min-w-0"
                            :key="`search-user-${searchFormKey}`"
                            :filter-option="false"
                            @search="searchUserForList"
                            @select="
                                (value, option) => {
                                    searchForm.user_id = option.userId || '';
                                    searchForm.user_id_display =
                                        option.label || value;
                                }
                            "
                            @change="
                                (value) => {
                                    const picked = userIdOptions.find(
                                        (option) =>
                                            String(option.userId) ===
                                                String(searchForm.user_id) &&
                                            option.label === value
                                    );
                                    if (!value || !picked) {
                                        searchForm.user_id = '';
                                    }
                                }
                            "
                        />
                    </a-form-item>

                    <a-form-item label="對內名稱" class="!mb-0">
                        <a-input
                            v-model:value="searchForm.internal_name"
                            placeholder="名稱"
                            allowClear
                            class="w-full min-w-0"
                        />
                    </a-form-item>

                    <a-form-item label="狀態" class="!mb-0">
                        <a-select
                            v-model:value="searchForm.status"
                            placeholder="全部"
                            allowClear
                            class="w-full min-w-0"
                        >
                            <a-select-option value="">全部</a-select-option>
                            <a-select-option :value="0">
                                編輯中
                            </a-select-option>
                            <a-select-option :value="1">
                                完成
                            </a-select-option>
                        </a-select>
                    </a-form-item>

                    <a-form-item label="預約" class="!mb-0">
                        <a-select
                            v-model:value="searchForm.requires_booking"
                            placeholder="全部"
                            allowClear
                            class="w-full min-w-0"
                        >
                            <a-select-option value="">全部</a-select-option>
                            <a-select-option :value="1">是</a-select-option>
                            <a-select-option :value="0">否</a-select-option>
                        </a-select>
                    </a-form-item>

                    <a-form-item
                        label="淨資產"
                        class="!mb-0 filter-range-item"
                    >
                        <div class="filter-range">
                            <a-input-number
                                v-model:value="searchForm.net_asset_value_min"
                                :min="0"
                                class="filter-range__input"
                                placeholder="最小"
                            />
                            <span class="filter-range__sep">~</span>
                            <a-input-number
                                v-model:value="searchForm.net_asset_value_max"
                                :min="0"
                                class="filter-range__input"
                                placeholder="最大"
                            />
                        </div>
                    </a-form-item>

                    <a-form-item
                        label="月現金流"
                        class="!mb-0 filter-range-item"
                    >
                        <div class="filter-range">
                            <a-input-number
                                v-model:value="searchForm.monthly_cash_flow_min"
                                class="filter-range__input"
                                placeholder="最小"
                            />
                            <span class="filter-range__sep">~</span>
                            <a-input-number
                                v-model:value="searchForm.monthly_cash_flow_max"
                                class="filter-range__input"
                                placeholder="最大"
                            />
                        </div>
                    </a-form-item>

                    <div class="admin-filter-actions">
                        <a-button size="middle" @click="handleReset">
                            重置
                        </a-button>
                        <a-button
                            size="middle"
                            type="primary"
                            @click="handleSearch"
                        >
                            <template #icon><search-outlined /></template>
                            查詢
                        </a-button>
                    </div>
                </div>
            </a-form>
        </a-card>

        <!-- 列表 -->
        <DataTable
            title="版本列表"
            :columns="columns"
            :data-source="tableData"
            :pagination="pagination"
            :scroll="{ x: 1380 }"
            :loading="loading"
            @refresh="getVersionList(pagination.current, pagination.pageSize)"
            @change="
                ({ pagination }) =>
                    handlePaginationChange(
                        pagination.current,
                        pagination.pageSize
                    )
            "
        >
            <template #titleContent>
                <div class="version-legend">
                    <span class="version-legend__item">
                        <span
                            class="version-legend__swatch version-legend__swatch--editing"
                        />
                        <span class="version-legend__text">編輯中</span>
                    </span>
                    <span class="version-legend__item">
                        <span
                            class="version-legend__swatch version-legend__swatch--done"
                        />
                        <span class="version-legend__text">完成</span>
                    </span>
                    <span class="version-legend__item">
                        <a-tag color="orange" class="m-0">預</a-tag>
                    </span>
                    <span class="version-legend__item">
                        <a-tag color="gold" class="m-0">已諮詢</a-tag>
                    </span>
                    <span class="version-legend__item">
                        <a-tag color="cyan" class="m-0">諮詢中</a-tag>
                    </span>
                </div>
            </template>

            <!-- 新增按鈕放在表格標題右側 -->
            <template #titleExtra>
                <a-button v-if="canCreate" type="primary" @click="handleAdd">
                    <template #icon><plus-outlined /></template>
                    新增版本
                </a-button>
            </template>
            <template #bodyCell="{ column, record }">
                <template v-if="column.key === 'total_assets'">
                    <span class="finance-num">
                        {{ formatNumber(record.total_assets) }}
                    </span>
                </template>
                <template v-else-if="column.key === 'total_liabilities'">
                    <span class="finance-num finance-num--negative">
                        {{ formatNumber(record.total_liabilities) }}
                    </span>
                </template>
                <template v-else-if="column.key === 'names'">
                    <div
                        class="version-name-cell"
                        :class="{
                            'is-editing': record.status === '編輯中',
                            'is-done': record.status === '完成'
                        }"
                    >
                        <div class="version-name-cell__row">
                            <span
                                class="version-name-cell__title"
                                :title="
                                    [
                                        record.internal_name || '（未命名）',
                                        record.status
                                    ]
                                        .filter(Boolean)
                                        .join(' · ')
                                "
                            >
                                {{ record.internal_name || '（未命名）' }}
                            </span>
                            <span
                                v-if="
                                    record.requires_booking === 1 ||
                                    record.has_consultation ||
                                    record.has_consultation_in_progress
                                "
                                class="version-name-cell__tags"
                            >
                                <a-tag
                                    v-if="record.requires_booking === 1"
                                    color="orange"
                                    class="m-0 version-tag"
                                >
                                    預
                                </a-tag>
                                <a-tag
                                    v-if="record.has_consultation"
                                    color="gold"
                                    class="m-0 version-tag"
                                >
                                    已諮詢
                                </a-tag>
                                <a-tag
                                    v-if="record.has_consultation_in_progress"
                                    color="cyan"
                                    class="m-0 version-tag"
                                >
                                    諮詢中
                                </a-tag>
                            </span>
                        </div>
                        <div
                            v-if="distinctExternalName(record)"
                            class="version-name-cell__subtitle"
                            :title="distinctExternalName(record)"
                        >
                            {{ distinctExternalName(record) }}
                        </div>
                        <template
                            v-for="meta in [bookingMeta(record)]"
                            :key="`booking-${record.id}`"
                        >
                            <button
                                v-if="meta"
                                type="button"
                                class="version-name-cell__booking"
                                :title="`${meta.title}（點擊查看預約細節）`"
                                @click="goConsultationFromVersion(record, $event)"
                            >
                                <span
                                    v-if="meta.consultant"
                                    class="version-name-cell__booking-name"
                                >
                                    {{ meta.consultant }}
                                </span>
                                <span class="version-name-cell__booking-time">
                                    {{ meta.when
                                    }}<template v-if="meta.mode"
                                        > · {{ meta.mode }}</template
                                    >
                                </span>
                            </button>
                        </template>
                    </div>
                </template>
                <template v-else-if="column.key === 'user_name'">
                    <span class="member-name">{{ record.user_name || '—' }}</span>
                </template>
                <template v-else-if="column.key === 'net_asset_value'">
                    <div
                        class="finance-comparison"
                        :class="{
                            'finance-comparison--single': !adjustmentFor(
                                record,
                                'netAsset'
                            )
                        }"
                    >
                        <span
                            class="finance-num"
                            :class="
                                cashFlowClass(
                                    adjustmentFor(record, 'netAsset')?.before ??
                                        record.net_asset_value
                                )
                            "
                        >
                            {{
                                formatNumber(
                                    adjustmentFor(record, 'netAsset')?.before ??
                                        record.net_asset_value
                                )
                            }}
                        </span>
                        <template v-if="adjustmentFor(record, 'netAsset')">
                            <span class="finance-comparison__arrow" aria-hidden="true"
                                >→</span
                            >
                            <span
                                class="finance-num"
                                :class="
                                    cashFlowClass(
                                        adjustmentFor(record, 'netAsset').after
                                    )
                                "
                            >
                                {{
                                    formatNumber(
                                        adjustmentFor(record, 'netAsset').after
                                    )
                                }}
                            </span>
                        </template>
                    </div>
                </template>
                <template v-else-if="column.key === 'total_monthly_income'">
                    <span class="finance-num finance-num--positive">
                        {{ formatNumber(record.total_monthly_income) }}
                    </span>
                </template>
                <template v-else-if="column.key === 'total_monthly_expenses'">
                    <span class="finance-num finance-num--negative">
                        {{ formatNumber(record.total_monthly_expenses) }}
                    </span>
                </template>
                <template v-else-if="column.key === 'monthly_cash_flow'">
                    <div
                        class="finance-comparison"
                        :class="{
                            'finance-comparison--single': !adjustmentFor(
                                record,
                                'cashFlow'
                            )
                        }"
                    >
                        <span
                            class="finance-num"
                            :class="
                                cashFlowClass(
                                    adjustmentFor(record, 'cashFlow')?.before ??
                                        record.monthly_cash_flow
                                )
                            "
                        >
                            {{
                                formatNumber(
                                    adjustmentFor(record, 'cashFlow')?.before ??
                                        record.monthly_cash_flow
                                )
                            }}
                        </span>
                        <template v-if="adjustmentFor(record, 'cashFlow')">
                            <span
                                class="finance-comparison__arrow"
                                aria-hidden="true"
                                >→</span
                            >
                            <span
                                class="finance-num"
                                :class="
                                    cashFlowClass(
                                        adjustmentFor(record, 'cashFlow').after
                                    )
                                "
                            >
                                {{
                                    formatNumber(
                                        adjustmentFor(record, 'cashFlow').after
                                    )
                                }}
                            </span>
                        </template>
                    </div>
                </template>
                <template v-else-if="column.key === 'created_at'">
                    <span class="datetime-cell">
                        {{ formatDateTime(record.created_at) }}
                    </span>
                </template>
                <template v-else-if="column.key === 'updated_at'">
                    <span class="datetime-cell">
                        {{ formatDateTime(record.updated_at) }}
                    </span>
                </template>
                <template
                    v-else-if="
                        column.key === 'actions' && hasAnyActionPermission
                    "
                >
                    <div class="admin-table-actions">
                        <a-button
                            v-if="canView"
                            size="small"
                            class="admin-table-actions__btn"
                            @click="handleOpen(record)"
                        >
                            開啟
                        </a-button>
                        <a-button
                            v-if="canDelete"
                            danger
                            size="small"
                            class="admin-table-actions__btn"
                            @click="handleDelAction(record)"
                        >
                            刪除
                        </a-button>
                    </div>
                </template>
            </template>
        </DataTable>

        <!-- 新增版本彈窗 -->
        <a-modal
            :open="addOpen"
            title="新增版本"
            :confirm-loading="addLoading"
            @ok="submitAdd"
            @cancel="
                () => {
                    addOpen = false;
                    resetAddForm();
                }
            "
            :mask-closable="false"
            :width="560"
            ok-text="確認"
            cancel-text="取消"
            wrap-class-name="add-version-modal"
            :centered="true"
            :keyboard="true"
        >
            <div class="add-version">
                <div class="add-version__mode">
                    <a-radio-group
                        v-model:value="addTab"
                        button-style="solid"
                        class="add-version__mode-group"
                    >
                        <a-radio-button value="new">全新建立</a-radio-button>
                        <a-radio-button v-if="canCopy" value="copy"
                            >複製既有</a-radio-button
                        >
                    </a-radio-group>
                </div>

                <a-form
                    ref="addFormRef"
                    layout="vertical"
                    class="add-version__form"
                    :model="addForm"
                >
                    <div class="add-version__grid">
                        <a-form-item
                            label="學員"
                            class="add-version__item add-version__item--full"
                            required
                        >
                            <a-select
                                v-model:value="addUserId"
                                show-search
                                :options="addUserOptions"
                                :loading="addUserLoading"
                                placeholder="輸入學員姓名搜尋"
                                allow-clear
                                :disabled="addLoading"
                                :filter-option="
                                    (input, option) =>
                                        String(option.label || '')
                                            .toLowerCase()
                                            .includes(
                                                String(input || '').toLowerCase()
                                            )
                                "
                                @search="searchUsers"
                                @change="
                                    (val) => {
                                        if (val) {
                                            searchVersions('');
                                        } else {
                                            addCopyVersionId.value = '';
                                            addCopyVersionDisplay.value = '';
                                            addCopyVersionOptions.value = [];
                                        }
                                    }
                                "
                            />
                        </a-form-item>

                        <a-form-item
                            v-if="addTab === 'copy'"
                            label="複製來源版本"
                            class="add-version__item add-version__item--full"
                            required
                        >
                            <a-select
                                v-model:value="addCopyVersionId"
                                :options="addCopyVersionOptions"
                                :loading="addCopyLoading"
                                :placeholder="
                                    addUserId
                                        ? '選擇要複製的版本'
                                        : '請先選擇學員'
                                "
                                :disabled="!addUserId || addLoading"
                                allow-clear
                                show-search
                                :filter-option="false"
                                @search="searchVersions"
                                @change="
                                    (val) => {
                                        if (!val) {
                                            addCopyVersionDisplay.value = '';
                                            return;
                                        }
                                        const selectedOption =
                                            addCopyVersionOptions.value.find(
                                                (opt) => opt.value === val
                                            );
                                        addCopyVersionDisplay.value =
                                            selectedOption?.label || '';
                                    }
                                "
                            />
                            <p
                                v-if="
                                    addUserId &&
                                    !addCopyLoading &&
                                    addCopyVersionOptions.length === 0
                                "
                                class="add-version__hint"
                            >
                                此學員尚無可複製的版本
                            </p>
                        </a-form-item>

                        <a-form-item
                            label="對外名稱"
                            class="add-version__item"
                        >
                            <a-input
                                v-model:value="addForm.external_name"
                                placeholder="學員可見名稱"
                                :disabled="addLoading"
                            />
                        </a-form-item>
                        <a-form-item
                            label="對內名稱"
                            class="add-version__item"
                        >
                            <a-input
                                v-model:value="addForm.internal_name"
                                placeholder="後台識別名稱"
                                :disabled="addLoading"
                            />
                        </a-form-item>
                        <p class="add-version__hint add-version__item--full">
                            對外／對內名稱至少填一項
                        </p>

                        <a-form-item
                            label="前台可見"
                            class="add-version__item add-version__item--full add-version__switch"
                        >
                            <div class="add-version__switch-row">
                                <a-switch
                                    v-model:checked="addForm.is_visible_user"
                                    :disabled="addLoading"
                                />
                                <span class="add-version__switch-text">
                                    {{
                                        addForm.is_visible_user
                                            ? '學員可在前台看到此版本'
                                            : '學員前台不可見'
                                    }}
                                </span>
                            </div>
                        </a-form-item>
                    </div>

                    <template v-if="addTab === 'new'">
                        <div class="add-version__section-label">個人資訊</div>
                        <div class="add-version__grid">
                            <a-form-item label="年齡" class="add-version__item">
                                <a-input-number
                                    v-model:value="addPersonal.age"
                                    :min="0"
                                    :max="120"
                                    placeholder="年齡"
                                    :disabled="addLoading"
                                    class="add-version__number"
                                />
                            </a-form-item>
                            <a-form-item label="職業" class="add-version__item">
                                <a-input
                                    v-model:value="addPersonal.job"
                                    placeholder="職業"
                                    :disabled="addLoading"
                                />
                            </a-form-item>
                            <a-form-item
                                label="婚姻狀態"
                                class="add-version__item"
                            >
                                <a-select
                                    v-model:value="addPersonal.marital_status"
                                    :disabled="addLoading"
                                >
                                    <a-select-option :value="0"
                                        >未婚</a-select-option
                                    >
                                    <a-select-option :value="1"
                                        >已婚</a-select-option
                                    >
                                </a-select>
                            </a-form-item>
                            <a-form-item
                                label="子女數"
                                class="add-version__item"
                            >
                                <a-input-number
                                    v-model:value="addPersonal.children"
                                    :min="0"
                                    :max="20"
                                    placeholder="子女數"
                                    :disabled="addLoading"
                                    class="add-version__number"
                                />
                            </a-form-item>
                        </div>
                    </template>
                    <p v-else class="add-version__copy-note">
                        複製模式會沿用來源版本的個人資訊。
                    </p>
                </a-form>
            </div>
        </a-modal>
    </div>
</template>

<style scoped>
.finance-version-list {
    max-width: 1600px;
}

.version-filter-grid {
    grid-template-columns: repeat(auto-fill, minmax(148px, 1fr));
}

.version-filter-grid :deep(.ant-picker) {
    width: 100%;
}

.filter-range-item {
    grid-column: span 2;
    min-width: 0;
}

.filter-range {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
}

.filter-range__input {
    flex: 1;
    min-width: 0;
    width: 100% !important;
}

.filter-range__sep {
    flex-shrink: 0;
    color: #94a3b8;
    font-size: 12px;
}

.add-version {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.add-version__mode-group {
    display: flex;
    width: 100%;
}

.add-version__mode-group :deep(.ant-radio-button-wrapper) {
    flex: 1;
    text-align: center;
}

.add-version__form :deep(.ant-form-item) {
    margin-bottom: 0;
}

.add-version__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px 12px;
}

.add-version__item--full {
    grid-column: 1 / -1;
}

.add-version__section-label {
    margin: 4px 0 2px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: #64748b;
}

.add-version__hint {
    margin: -4px 0 0;
    font-size: 12px;
    line-height: 1.4;
    color: #94a3b8;
}

.add-version__switch-row {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 32px;
}

.add-version__switch-text {
    font-size: 13px;
    color: #64748b;
}

.add-version__number {
    width: 100%;
}

.add-version__copy-note {
    margin: 0;
    padding: 10px 12px;
    border-radius: 10px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    font-size: 13px;
    color: #64748b;
}

.version-legend {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 10px;
    margin-left: 4px;
}

.version-legend__item {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.version-legend__text {
    font-size: 12px;
    color: #64748b;
}

.version-legend__swatch {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 999px;
    flex: 0 0 auto;
}

.version-legend__swatch--editing {
    background: #f59e0b;
    box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.2);
}

.version-legend__swatch--done {
    background: #16a34a;
    box-shadow: 0 0 0 2px rgba(22, 163, 74, 0.18);
}

.version-name-cell {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    min-width: 0;
    text-align: left;
    line-height: 1.4;
}

.version-name-cell__row {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    gap: 6px;
    min-width: 0;
    max-width: 100%;
}

.version-name-cell__title {
    flex: 0 1 auto;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 600;
    color: #0f172a;
}

.version-name-cell.is-editing .version-name-cell__title {
    color: #c2410c;
}

.version-name-cell.is-done .version-name-cell__title {
    color: #166534;
}

.version-name-cell__tags {
    display: inline-flex;
    flex: 0 0 auto;
    flex-wrap: nowrap;
    align-items: center;
    gap: 4px;
}

.version-name-cell__subtitle {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
    color: #64748b;
}

.version-name-cell__booking {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1px;
    max-width: 100%;
    min-width: 0;
    width: 100%;
    margin: 1px 0 0;
    padding: 4px 0 0;
    border: 0;
    border-top: 1px solid #e2e8f0;
    background: transparent;
    text-align: left;
    cursor: pointer;
    font: inherit;
    font-size: 12px;
    line-height: 1.35;
    color: #0f766e;
    font-variant-numeric: tabular-nums;
}

.version-name-cell__booking:hover .version-name-cell__booking-name,
.version-name-cell__booking:hover .version-name-cell__booking-time {
    text-decoration: underline;
    text-underline-offset: 2px;
}

.version-name-cell__booking:focus-visible {
    outline: 2px solid #99f6e4;
    outline-offset: 2px;
    border-radius: 4px;
}

.version-name-cell__booking-name,
.version-name-cell__booking-time {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.version-name-cell__booking-name {
    font-weight: 500;
    color: #0f766e;
}

.version-name-cell__booking-time {
    color: #64748b;
    font-weight: 400;
}

.version-tag.ant-tag {
    margin-inline-end: 0;
    padding: 0 6px;
    font-size: 12px;
    line-height: 20px;
}

.member-name {
    font-weight: 500;
    color: #334155;
}

.finance-num {
    display: inline-block;
    font-variant-numeric: tabular-nums;
    font-feature-settings: 'tnum';
    color: #334155;
}

.finance-num--positive {
    color: #15803d;
}

.finance-num--negative {
    color: #dc2626;
}

.finance-num--neutral {
    color: #64748b;
}

.finance-comparison {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    white-space: nowrap;
    width: 100%;
}

.finance-comparison__arrow {
    flex: 0 0 auto;
    padding: 0 2px;
    font-size: 11px;
    font-weight: 500;
    color: #cbd5e1;
    line-height: 1;
}

.datetime-cell {
    font-size: 12px;
    color: #64748b;
    white-space: nowrap;
}

.action-btn {
    min-width: 56px;
}

@media (max-width: 768px) {
    .filter-range-item {
        grid-column: span 1;
    }

    .version-legend {
        display: none;
    }
}
</style>
